import { DetailedOpeningHours } from '@/types/terrace';

export const isTerraceCurrentlyOpen = (
  openingHours: string | undefined,
  detailedOpeningHours: DetailedOpeningHours | undefined
): boolean => {
  // If we have detailed opening hours, use those for accurate checking
  if (detailedOpeningHours) {
    return isOpenWithDetailedHours(detailedOpeningHours);
  }
  
  // Fallback to basic opening hours string parsing
  if (openingHours) {
    return isOpenWithBasicHours(openingHours);
  }
  
  // If no opening hours info available, assume closed
  return false;
};

const isOpenWithDetailedHours = (detailedHours: DetailedOpeningHours): boolean => {
  const now = new Date();
  const currentDay = getDayName(now.getDay());
  const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes since midnight
  
  const daySchedule = detailedHours[currentDay];
  
  if (!daySchedule || !daySchedule.isOpen) {
    return false;
  }
  
  const openTime = timeStringToMinutes(daySchedule.from);
  const closeTime = timeStringToMinutes(daySchedule.to);
  
  if (openTime === null || closeTime === null) {
    return false;
  }
  
  // Handle cases where closing time is next day (e.g., 02:00)
  if (closeTime < openTime) {
    return currentTime >= openTime || currentTime <= closeTime;
  }
  
  return currentTime >= openTime && currentTime <= closeTime;
};

const isOpenWithBasicHours = (openingHours: string): boolean => {
  // This is a simple fallback for basic opening hours strings
  // You can enhance this based on your specific opening hours format
  const lowerHours = openingHours.toLowerCase();
  
  // If it says "closed" or similar, definitely closed
  const closedTerms = ['closed', 'stängt', 'stängd', 'stängda', 'ej öppet', 'ej öppen', 'håller stängt'];
  if (closedTerms.some(term => lowerHours.includes(term))) {
    return false;
  }
  
  // If it mentions "24" or "dygnet", assume open 24/7
  if (lowerHours.includes('24') || lowerHours.includes('dygnet')) {
    return true;
  }
  
  // Try to parse simple time ranges like "10:00-22:00"
  const timeRangeMatch = openingHours.match(/(\d{1,2}):?(\d{2})?\s*-\s*(\d{1,2}):?(\d{2})?/);
  if (timeRangeMatch) {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const openHour = parseInt(timeRangeMatch[1]);
    const openMinute = parseInt(timeRangeMatch[2] || '0');
    const closeHour = parseInt(timeRangeMatch[3]);
    const closeMinute = parseInt(timeRangeMatch[4] || '0');
    
    const openTime = openHour * 60 + openMinute;
    const closeTime = closeHour * 60 + closeMinute;
    
    if (closeTime < openTime) {
      return currentTime >= openTime || currentTime <= closeTime;
    }
    
    return currentTime >= openTime && currentTime <= closeTime;
  }
  
  // If we can't parse the opening hours, assume closed for safety
  return false;
};

const getDayName = (dayIndex: number): keyof DetailedOpeningHours => {
  const days: (keyof DetailedOpeningHours)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  return days[dayIndex];
};

const timeStringToMinutes = (timeString: string): number | null => {
  if (!timeString) return null;
  
  const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }
  
  return hours * 60 + minutes;
};